/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function PlayerEmbed() {
  return (
    <>
      <Meta
        title="Player SDK: Embed Options"
        description="Get descriptions of all the options for controlling the properties of an embedded Vimeo player through the player SDK."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Link href={'/player/sdk/embed'}>Player SDK</Path.Link>
            <Path.Current>Embed options</Path.Current>
          </Path>
        }
        nav={{ page: 'player-sdk', subpage: 'embed-options' }}
      >
        <Header id="embed-options">Embed Options</Header>
        <Paragraph size="2">
          Use the options in{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          to control the properties of an embedded Vimeo player, either as objects passed to the{' '}
          <code>Vimeo.Player</code> JavaScript constructor or through HTML attributes in a{' '}
          <code>{`<div>`}</code> element.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> We strongly recommend the JavaScript method. Use the HTML method
          only as a last resort.
        </StyledNotice>

        <GuideTable
          tableNumber={1}
          description="Embed options"
          headers={['Option', 'Default value', 'Description']}
          rows={[
            [
              <span>
                <code>
                  <b>id</b>
                </code>{' '}
                <em>or</em>{' '}
                <code>
                  <b>url</b>
                </code>
              </span>,
              'None',
              <span>
                The ID or the URL of the video on Vimeo. You must supply one of these values to
                identify the video. When the video's{' '}
                <Link
                  variant="minimal"
                  href="https://vimeo.com/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426199699985-Overview-of-video-privacy-settings"
                >
                  privacy setting
                </Link>{' '}
                is <code>Unlisted</code>, you must use the URL, and the URL must include the{' '}
                <code>
                  <b>h</b>
                </code>{' '}
                parameter. For more information, see our{' '}
                <Link variant="minimal" href="/player/sdk/basics#connecting-to-a-vimeo-player">
                  introductory guide
                </Link>
                .
              </span>,
            ],
            [
              <span>
                <code>
                  <b>airplay</b>
                </code>
              </span>,
              <code>true</code>,
              <span>Whether AirPlay is enabled in the embeddable player.</span>,
            ],
            [
              <span>
                <code>
                  <b>audio_tracks</b>
                </code>
              </span>,
              <code>true</code>,
              "Whether multiple audio tracks can appear in the embeddable player. This option has no effect if your video doesn't have additional audio tracks uploaded to it.",
            ],
            [
              <span>
                <code>
                  <b>audiotrack</b>
                </code>
              </span>,
              <span>None</span>,
              <span>
                The initial audio track to play with the video. The value of this option can be the
                language code alone (<code>fr</code>), the language code and region (
                <code>fr-ca</code>), or the language code and kind (<code>fr,descriptions</code>) of
                the desired audio. Set this value to <code>main</code> to select the original audio
                for the video. If there is no matching track, the player ignores this value.
              </span>,
            ],
            [
              <span>
                <code>
                  <b>autopause</b>
                </code>
              </span>,
              <code>true</code>,
              <span>
                Whether to pause the current video when another Vimeo video on the same page starts
                to play. Set this value to <code>false</code> to permit simultaneous playback of all
                the videos on the page. This option has no effect if you've disabled cookies in your
                browser, either through browser settings or with an extension or plugin.
              </span>,
            ],
            [
              <code>
                <b>autoplay</b>
              </code>,
              <code>false</code>,
              'Whether to start playback of the video automatically. This feature might not work on all devices.',
            ],
            [
              <code>
                <b>background</b>
              </code>,
              <code>false</code>,
              'Whether the player is in background mode, which hides the playback controls, enables autoplay, and loops the video.',
            ],
            [
              <code>
                <b>byline</b>
              </code>,
              <code>true</code>,
              "Whether to display the video owner's name.",
            ],
            [
              <code>
                <b>cc</b>
              </code>,
              <code>true</code>,
              "Whether closed captions are enabled in the embeddable player. This option has no effect if your video doesn't have captions or subtitles.",
            ],
            [
              <code>
                <b>chapter_id</b>
              </code>,
              'None',
              'The chapter by ID to start playback at.',
            ],
            [
              <code>
                <b>chapters</b>
              </code>,
              <code>true</code>,
              "Whether chapters are enabled in the embeddable player. This option has no effect if your video doesn't have chapters set.",
            ],
            [
              <code>
                <b>chromecast</b>
              </code>,
              <code>true</code>,
              'Whether the Chromecast button appears in the embeddable player.',
            ],
            [
              <code>
                <b>color</b>
              </code>,
              'None',
              <span>
                The hexadecimal accent color value of the playback controls, which is normally{' '}
                <code>00ADEF</code>. The embed settings of the video might override this value. For
                more information about player colors, refer to the <code>colors</code> option.
              </span>,
            ],
            [
              <code>
                <b>colors</b>
              </code>,
              'None',
              <span>
                The hexadecimal color values of the player. The embed settings of the video might
                override these values. The order of the player colors is{' '}
                <code>[Primary, Accent, Text/Icon, Background]</code>, with corresponding default
                values of <code>["000000", "00ADEF", "FFFFFF", "000000"]</code>.
              </span>,
            ],
            [
              <code>
                <b>controls</b>
              </code>,
              <code>true</code>,
              <span>
                Whether to display the player's interactive elements, including the play bar and
                sharing buttons. Set this option to <code>false</code> for a chromeless experience.
                To control playback when the play/pause button is hidden, set{' '}
                <code>
                  <b>autoplay</b>
                </code>{' '}
                to <code>true</code>, use keyboard controls (which remain active), or implement our
                player SDK.
              </span>,
            ],
            [
              <code>
                <b>dnt</b>
              </code>,
              <code>false</code>,
              <span>
                Whether to prevent the player from tracking session data, including cookies. Keep in
                mind that setting this argument to <code>true</code> also blocks{' '}
                <Link variant="minimal" href="https://vimeo.com/stats">
                  video stats
                </Link>
                .
              </span>,
            ],
            [
              <code>
                <b>end_time</b>
              </code>,
              'None',
              <span>
                In Segmented Playback mode, the time in seconds where playback ends for the video.
                Setting this field enables Segmented Playback mode, where the video is presented as
                a segment of the original as defined by this field and the{' '}
                <code>
                  <b>start_time</b>
                </code>{' '}
                field, with a duration in seconds equal to{' '}
                <code>
                  <b>end_time</b>
                </code>{' '}
                minus{' '}
                <code>
                  <b>start_time</b>
                </code>
                . If{' '}
                <code>
                  <b>start_time</b>
                </code>{' '}
                is omitted, the start time defaults to <code>0</code>.
              </span>,
            ],
            [
              <code>
                <b>fullscreen</b>
              </code>,
              <code>true</code>,
              <span>Whether to show the fullscreen button in the embeddable player.</span>,
            ],
            [
              <code>
                <b>height</b>
              </code>,
              'None',
              'The height of the video in pixels.',
            ],
            [
              <code>
                <b>initial_quality</b>
              </code>,
              'None',
              <span>
                The playback quality of the first seconds of video loaded. After that, normal
                adaptive bitrate logic applies. Setting a lower quality can help to make playback
                start more quickly, while setting a higher quality helps to ensure that viewers
                receive high-resolution video as soon as the video begins playing. This field can
                take any value from the quality menu in the player for the video. Common values are{' '}
                <code>360p</code>, <code>720p</code>, <code>1080p</code>, <code>2k</code>, and{' '}
                <code>4k</code>.
              </span>,
            ],
            [
              <code>
                <b>interactive_markers</b>
              </code>,
              <code>true</code>,
              'Whether to display markers representing the timestamp where hotspots appear on an interactive video.',
            ],
            [
              <code>
                <b>interactive_params</b>
              </code>,
              'None',
              <span>
                Key-value pairs representing dynamic parameters that are utilized on interactive
                videos with dynamic elements, such as{' '}
                <code>title=my-video,subtitle=interactive</code>.
              </span>,
            ],
            [
              <code>
                <b>keyboard</b>
              </code>,
              <code>true</code>,
              "Whether to enable keyboard input to trigger player events. This setting doesn't affect tab control.",
            ],
            [
              <code>
                <b>loop</b>
              </code>,
              <code>false</code>,
              'Whether to restart the video automatically after reaching the end.',
            ],
            [
              <code>
                <b>maxheight</b>
              </code>,
              'None',
              "The height of the video in pixels, where the video won't exceed its native height, no matter the value of this field.",
            ],
            [
              <code>
                <b>max_quality</b>
              </code>,
              'None',
              <span>
                The highest possible quality that the player automatically switches to during video
                playback. The player determines the best quality to play at without exceeding this
                maximum value. Setting this parameter can help to reduce bandwidth usage. This field
                can take any value from the quality menu in the player for the video. Common values
                are <code>360p</code>, <code>720p</code>, <code>1080p</code>, <code>2k</code>, or{' '}
                <code>4k</code>.
              </span>,
            ],
            [
              <code>
                <b>maxwidth</b>
              </code>,
              'None',
              "The width of the video in pixels, where the video won't exceed its native width, no matter the value of this field.",
            ],
            [
              <code>
                <b>min_quality</b>
              </code>,
              'None',
              <span>
                The lowest possible quality that the player automatically switches to during video
                playback. The player determines the best quality to play at without dropping below
                this minimum value. Setting this parameter can help to ensure that the viewer
                receives high-resolution video while still accounting for bandwidth. This field can
                take any value from the quality menu in the player for the video. Common values are{' '}
                <code>360p</code>, <code>720p</code>, <code>1080p</code>, <code>2k</code>, or{' '}
                <code>4k</code>.
              </span>,
            ],
            [
              <code>
                <b>muted</b>
              </code>,
              <code>false</code>,
              <span>
                Whether the video is muted upon loading. The <code>true</code> value is required for
                the autoplay behavior in some browsers.
              </span>,
            ],
            [
              <code>
                <b>pip</b>
              </code>,
              <code>true</code>,
              'Whether to include the picture-in-picture button among the player controls and enable the Picture-in-Picture API, where supported.',
            ],
            [
              <code>
                <b>play_button_position</b>
              </code>,
              <code>auto</code>,
              <span>
                The position of the play button within the embeddable player. Other possible values
                include <code>bottom</code> and <code>center</code>.
              </span>,
            ],
            [
              <code>
                <b>playsinline</b>
              </code>,
              <code>true</code>,
              <span>
                Whether the video plays inline on supported mobile devices. To force the device to
                play the video in fullscreen mode instead, set this value to <code>false</code>.
              </span>,
            ],
            [
              <code>
                <b>portrait</b>
              </code>,
              <code>true</code>,
              "Whether to display the video owner's portrait.",
            ],
            [
              <code>
                <b>preload</b>
              </code>,
              <code>metadata_on_hover</code>,
              <span>
                The loading behavior for the player before playback is initiated. This follows the
                basic principles of the native{' '}
                <Link
                  variant="minimal"
                  href="https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/preload"
                >
                  preload property
                </Link>{' '}
                of the <code>HTMLMediaElement</code> interface, with a few additional values:
                <ul>
                  <li>
                    Specify <code>metadata</code> to load metadata immediately.
                  </li>
                  <li>
                    Specify <code>metadata_on_hover</code> to load metadata for the video only when
                    the user hovers over the player.
                  </li>
                  <li>
                    Specify <code>auto</code> to load metadata and the first few seconds of video
                    immediately.
                  </li>
                  <li>
                    Specify <code>auto_on_hover</code> to load metadata and the first few seconds of
                    video only when the user hovers over the player.
                  </li>
                  <li>
                    Specify <code>none</code> if you're not loading any data until playback begins.
                  </li>
                </ul>
                Preloading more data makes playback start more quickly, so using the{' '}
                <code>auto</code> and <code>auto_on_hover</code> values results in quicker startup
                than the other options, but may use more bandwidth since video segments are loaded
                regardless of a user playing the video. To learn more about Vimeo’s bandwidth
                limits, visit our{' '}
                <Link
                  variant="minimal"
                  href="https://help.vimeo.com/hc/en-us/articles/12426275404305-Bandwidth-on-Vimeo"
                >
                  Help Center
                </Link>
                .
              </span>,
            ],
            [
              <code>
                <b>progress_bar</b>
              </code>,
              <code>true</code>,
              'Whether to show the progress bar in the embeddable player.',
            ],
            [
              <code>
                <b>quality</b>
              </code>,
              <code>auto</code>,
              <span>
                The playback quality of the video. Use <code>auto</code> for the best possible
                quality given available bandwidth and other factors. Otherwise this field can take
                any value from the quality menu in the player for the video. Common values are{' '}
                <code>360p</code>, <code>720p</code>, <code>1080p</code>, <code>2k</code>, and{' '}
                <code>4k</code>.
              </span>,
            ],
            [
              <code>
                <b>quality_selector</b>
              </code>,
              <code>true</code>,
              'Whether to show the quality selector in the embeddable player.',
            ],
            [
              <code>
                <b>responsive</b>
              </code>,
              <code>false</code>,
              <span>
                Whether to return a <em>responsive embed code</em>, or one that provides intelligent
                adjustments based on viewing conditions. We recommend this option for
                mobile-optimized sites.
              </span>,
            ],
            [
              <code>
                <b>skipping_forward</b>
              </code>,
              <code>true</code>,
              'Whether users can skip forward in the embeddable player.',
            ],
            [
              <code>
                <b>speed</b>
              </code>,
              <code>false</code>,
              'Whether to include playback speed among the player preferences.',
            ],
            [
              <code>
                <b>start_time</b>
              </code>,
              <code>0</code>,
              <span>
                In Segmented Playback mode, the time in seconds where playback starts for the video;
                see the{' '}
                <code>
                  <b>end_time</b>
                </code>{' '}
                field for more information.
              </span>,
            ],
            [
              <code>
                <b>texttrack</b>
              </code>,
              'None',
              <span>
                The text track to display with the video. Specify the text track by its language
                code (<code>en</code>), the language code and locale (<code>en-US</code>), or the
                language code and kind (<code>en.captions</code>). For this argument to work, the
                video must already have a text track of the given type; see our{' '}
                <Link
                  variant="minimal"
                  href="https://vimeo.com/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426156795409-Captions-and-subtitles"
                >
                  Help Center
                </Link>{' '}
                or{' '}
                <Link variant="minimal" href="/api/upload/texttracks">
                  Working with Text Track Uploads
                </Link>{' '}
                for more information.
                <div>
                  <br />
                  To enable automatically generated closed captions instead, provide the value{' '}
                  <code>en-x-autogen</code>. Please note that, at the present time, automatic
                  captions are always in English.
                </div>
              </span>,
            ],
            [
              <code>
                <b>thumbnail_id</b>
              </code>,
              'None',
              "The ID of the thumbnail to load from the video's available thumbnail set.",
            ],
            [
              <code>
                <b>title</b>
              </code>,
              <code>true</code>,
              'Whether to display the video’s title.',
            ],
            [
              <code>
                <b>transcript</b>
              </code>,
              <code>true</code>,
              'Whether transcript controls can appear in the embeddable player.',
            ],
            [
              <code>
                <b>transparent</b>
              </code>,
              <code>true</code>,
              'Whether the responsive player and transparent background are enabled.',
            ],
            [
              <code>
                <b>unmute_button</b>
              </code>,
              <code>true</code>,
              'Whether to display the unmute button.',
            ],
            [
              <code>
                <b>vimeo_logo</b>
              </code>,
              <code>true</code>,
              'Whether to show the Vimeo logo in the embeddable player.',
            ],
            [
              <code>
                <b>volume</b>
              </code>,
              <code>true</code>,
              'Whether to show the volume selector in the embeddable player.',
            ],
            [
              <code>
                <b>watch_full_video</b>
              </code>,
              <code>true</code>,
              <span>
                Whether to show the Watch Full Video button when Segmented Playback mode is enabled;
                see{' '}
                <code>
                  <b>start_time</b>
                </code>{' '}
                and{' '}
                <code>
                  <b>end_time</b>
                </code>{' '}
                above for more information.
              </span>,
            ],
            [
              <code>
                <b>width</b>
              </code>,
              'None',
              'The width of the video in pixels.',
            ],
          ]}
        />

        <Header size="2" id="embed-options-as-objects">
          Specifying embed options as objects passed to <code>Vimeo.Player</code>
        </Header>
        <Paragraph size="2">
          To specify embed options as objects passed to the <code>Vimeo.Player</code> JavaScript
          constructor:
        </Paragraph>

        <ol>
          <li>
            In the body of an HTML page, add a <code>{`<div>`}</code> element to represent the
            embedded Vimeo player, and set the{' '}
            <code>
              <b>id</b>
            </code>{' '}
            attribute to name the player, like this:
            <br />
            <br />
            <CodeBlock language="html">{`<div id="{name}"></div>`}</CodeBlock>
          </li>
          <li>
            Connect the player SDK with a <code>{`<script>`}</code> element.
          </li>
          <li>
            In the JavaScript, declare an <code>options</code> variable, and set either the{' '}
            <code>
              <b>id</b>
            </code>{' '}
            property to the ID of the video that you want to load into the player or the{' '}
            <code>
              <b>url</b>
            </code>{' '}
            property to the URL of this video.
            <StyledNotice>
              <strong>NOTE:</strong> If the video is unlisted, use{' '}
              <code>
                <b>url</b>
              </code>
              , not{' '}
              <code>
                <b>id</b>
              </code>
              , and be sure that the URL includes the{' '}
              <code>
                <b>h</b>
              </code>{' '}
              parameter.
            </StyledNotice>
          </li>
          <li>
            Set additional property values corresponding to the embed options that you want to use.
            Since these aren't HTML attributes, you don't need to prefix the property names with{' '}
            <code>
              <b>data-vimeo-</b>
            </code>
            .
          </li>
        </ol>

        <StyledNotice>
          <strong>NOTE:</strong> Always give the{' '}
          <code>
            <b>id</b>
          </code>{' '}
          or{' '}
          <code>
            <b>url</b>
          </code>{' '}
          property before you set any other embed options.
        </StyledNotice>

        <Paragraph size="2">
          Here's an example that sets the width of a video to 800 pixels.
        </Paragraph>

        <CodeBlock language="html">{`<html>
<head>
<title>Example</title>
</head>
<body>

<div id="myVideo"></div>

<script src="{url}"></script>
<script>
  var options = {
    url: "https://player.vimeo.com/video/76979871?h=8272103f6e",
    width: 800
  };

  var videoPlayer = new Vimeo.Player('myVideo', options);

  videoPlayer.on('play', function() {
    console.log('Played the video');
  });
</script>

</body>
</html>`}</CodeBlock>

        <Header size="2" id="embed-options-as-html-attrs">
          Specifying embed options as HTML attributes
        </Header>
        <Paragraph size="2">To specify embed options as HTML attributes:</Paragraph>

        <ol>
          <li>
            In the body of an HTML page, add a <code>{`<div>`}</code> element to represent the
            embedded Vimeo player, and set the{' '}
            <code>
              <b>id</b>
            </code>{' '}
            attribute to name the player, like this:
            <br />
            <br />
            <CodeBlock language="html">{`<div id="{name}"></div>`}</CodeBlock>
          </li>
          <li>
            Connect the player SDK with a <code>{`<script>`}</code> element.
          </li>
          <li>
            Indicate the video to load into the player:
            <ul>
              <li>
                <strong>To indicate the video by its ID</strong>, which you're free to do for any
                non-unlisted video, use the{' '}
                <code>
                  <b>data-vimeo-id</b>
                </code>{' '}
                attribute of the <code>{`<div>`}</code> element, like this:
                <br />
                <br />
                <CodeBlock language="html">{`<div id="{name}" data-vimeo-id="{video_id}"></div>`}</CodeBlock>
              </li>
              <li>
                <strong>To indicate the video by its URL</strong>, which you must do if the video is
                unlisted, use the{' '}
                <code>
                  <b>data-vimeo-url</b>
                </code>{' '}
                attribute of the <code>{`<div>`}</code> element, like this:
                <br />
                <br />
                <CodeBlock language="html">{`<div id="{name}" data-vimeo-url="{video_url}"></div>`}</CodeBlock>
              </li>
            </ul>
          </li>
          <li>
            Include additional embed options by prefixing their attribute names with{' '}
            <code>
              <b>data-vimeo-</b>
            </code>
            , like this:
            <br />
            <br />
            <CodeBlock language="html">{`<div id="{name}" data-vimeo-id="{video_id}" data-vimeo-autoplay="true" data-vimeo-loop="true"></div>`}</CodeBlock>
          </li>
        </ol>

        <StyledNotice>
          <strong>NOTE:</strong> Before specifying any of the other{' '}
          <code>
            <b>data-vimeo-</b>
          </code>{' '}
          attributes, you must first indicate the video to load into the player with either{' '}
          <code>
            <b>data-vimeo-id</b>
          </code>{' '}
          or{' '}
          <code>
            <b>data-vimeo-url</b>
          </code>
          .
        </StyledNotice>

        <Paragraph size="2">
          The following example sets the width of a video to 800 pixels, like in the example from
          the previous section, but this time using HTML attributes.
        </Paragraph>

        <CodeBlock language="html">{`<html>
<head>
<title>Example</title>
</head>
<body>

<div id="myVideo" data-vimeo-id="280815263" data-vimeo-width="800"></div>

<script src="{url}"></script>
<script>
  var videoPlayer = new Vimeo.Player('myVideo');
  videoPlayer.on('play', function() {
    console.log('Played the video');
  });
</script>

</body>
</html>`}</CodeBlock>
      </GuideWrapper>
    </>
  );
}
